import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

interface Account {
  parent: Account | null;
  balance: {
    $: {
      number: string;
      commodity: string;
      value: string;
      sign: string;
    };
  };
  name: string;
  fullName: string[];
}

interface AccountsData {
  [key: string]: Account;
}

interface LedgerTableProps {
  accounts: AccountsData;
}

export const LedgerTable: React.FC<LedgerTableProps> = () => {
  const { theme, generalLedger } = useAccelContext();
  const [expandedAccounts, setExpandedAccounts] = useState<string[]>([]);
  const accounts = generalLedger?.accounts;

  const toggleExpand = (accountName: string) => {
    setExpandedAccounts((prev) =>
      prev.includes(accountName)
        ? prev.filter((name) => name !== accountName)
        : [...prev, accountName]
    );
  };

  const renderAccount = (account: Account, depth: number = 0) => {
    const isExpanded = expandedAccounts.includes(account.name);
    const hasSubAccounts = Object.values(accounts).some(
      (subAccount) =>
        subAccount?.fullName?.length > account?.fullName?.length &&
        subAccount?.fullName?.slice(0, account?.fullName?.length)?.join(":") ===
          account?.fullName?.join(":")
    );

    return (
      <React.Fragment key={account.fullName.join(":")}>
        <TableRow>
          <TableCell sx={{ paddingLeft: `${depth * 20}px` }}>
            {hasSubAccounts && (
              <IconButton
                size="small"
                onClick={() => toggleExpand(account.name)}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
            <Typography
              sx={{
                color: theme?.text?.hex || "#000000",
                fontSize: 14,
                display: "inline-block",
                marginLeft: hasSubAccounts ? "8px" : "24px",
              }}
            >
              {account?.name}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography
              sx={{
                color: theme?.text?.hex || "#000000",
                fontSize: 14,
              }}
            >
              {account?.balance?.$.number}
            </Typography>
          </TableCell>
        </TableRow>
        {isExpanded &&
          Object.values(accounts)
            .filter(
              (subAccount) =>
                subAccount?.fullName?.length ===
                  account?.fullName?.length + 1 &&
                subAccount?.fullName
                  ?.slice(0, account?.fullName?.length)
                  ?.join(":") === account?.fullName?.join(":")
            )
            .map((subAccount) => renderAccount(subAccount, depth + 1))}
      </React.Fragment>
    );
  };

  const rootAccounts = Object.values(accounts || {}).filter(
    (account) => account && account.parent === null
  );

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell align="right">Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rootAccounts?.map((account) => renderAccount(account))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
